import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Send, CheckCircle, AlertTriangle, Bell, Settings } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function TeamsIntegrationPage() {
  const [webhookConfigured, setWebhookConfigured] = useState(false);
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    alert_type: 'info'
  });

  useEffect(() => {
    checkConfiguration();
  }, []);

  const checkConfiguration = async () => {
    try {
      const response = await api.get('/health');
      setWebhookConfigured(response.data.integrations?.teams === 'configured');
    } catch (error) {
      console.error('Failed to check Teams configuration');
    }
  };

  const sendTestNotification = async (e) => {
    e.preventDefault();
    setSending(true);
    setTestResult(null);
    
    try {
      const response = await api.post('/notifications/teams', {
        title: notificationForm.title || 'Test Notification',
        message: notificationForm.message || 'This is a test notification from Zentry GRC Platform',
        alert_type: notificationForm.alert_type,
        facts: {
          'Source': 'Zentry GRC',
          'Type': 'Test Notification',
          'Time': new Date().toLocaleString()
        }
      });
      
      setTestResult(response.data);
      if (response.data.status === 'sent') {
        toast.success('Notification sent to Teams!');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to send notification');
      setTestResult({ status: 'error', message: error.response?.data?.detail || 'Unknown error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Microsoft Teams Integration"
        subtitle="Send compliance alerts and notifications to your Teams channels"
        icon={<MessageSquare size={28} />}
      />

      {/* Configuration Status */}
      <div className={`rounded-sm p-6 mb-8 ${webhookConfigured ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-center gap-3 mb-2">
          {webhookConfigured ? (
            <CheckCircle size={24} className="text-emerald-600" />
          ) : (
            <AlertTriangle size={24} className="text-amber-600" />
          )}
          <h3 className={`font-semibold ${webhookConfigured ? 'text-emerald-900' : 'text-amber-900'}`}>
            {webhookConfigured ? 'Teams Integration Active' : 'Teams Webhook Not Configured'}
          </h3>
        </div>
        <p className={`text-sm ${webhookConfigured ? 'text-emerald-700' : 'text-amber-700'}`}>
          {webhookConfigured 
            ? 'Your Microsoft Teams webhook is configured. Notifications will be sent to your Teams channel.'
            : 'To enable Teams notifications, add your webhook URL to TEAMS_WEBHOOK_URL in the backend environment variables.'}
        </p>
        
        {!webhookConfigured && (
          <div className="mt-4 bg-white rounded-sm p-4 border border-amber-200">
            <h4 className="font-medium text-sm mb-2">How to get a Teams Webhook URL:</h4>
            <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
              <li>Open Microsoft Teams and go to your channel</li>
              <li>Click &quot;...&quot; (More options) → &quot;Connectors&quot;</li>
              <li>Find &quot;Incoming Webhook&quot; and click &quot;Configure&quot;</li>
              <li>Name it &quot;GRC Alerts&quot; and copy the webhook URL</li>
              <li>Add it to your backend .env as TEAMS_WEBHOOK_URL</li>
            </ol>
          </div>
        )}
      </div>

      {/* Send Test Notification */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-8">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Send size={20} />
          Send Test Notification
        </h3>
        
        <form onSubmit={sendTestNotification} className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              data-testid="teams-title-input"
              value={notificationForm.title}
              onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
              placeholder="GRC Alert: Test Notification"
              className="mt-1.5 rounded-sm"
            />
          </div>
          
          <div>
            <Label>Message</Label>
            <Textarea
              data-testid="teams-message-input"
              value={notificationForm.message}
              onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
              placeholder="This is a test notification from Zentry GRC Platform"
              rows={3}
              className="mt-1.5 rounded-sm"
            />
          </div>
          
          <div>
            <Label>Alert Type</Label>
            <Select
              value={notificationForm.alert_type}
              onValueChange={(value) => setNotificationForm({ ...notificationForm, alert_type: value })}
            >
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="teams-alert-type">
                <SelectValue placeholder="Select alert type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info (Blue)</SelectItem>
                <SelectItem value="success">Success (Green)</SelectItem>
                <SelectItem value="warning">Warning (Yellow)</SelectItem>
                <SelectItem value="critical">Critical (Red)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button
            type="submit"
            data-testid="send-teams-btn"
            disabled={sending}
            className="bg-purple-600 hover:bg-purple-700 rounded-sm"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send size={18} className="mr-2" />
                Send Test Notification
              </>
            )}
          </Button>
        </form>

        {testResult && (
          <div className={`mt-4 p-4 rounded-sm ${
            testResult.status === 'sent' ? 'bg-emerald-50 border border-emerald-200' :
            testResult.status === 'mock_sent' ? 'bg-blue-50 border border-blue-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {testResult.status === 'sent' && <CheckCircle size={18} className="text-emerald-600" />}
              {testResult.status === 'mock_sent' && <Bell size={18} className="text-blue-600" />}
              {testResult.status === 'error' && <AlertTriangle size={18} className="text-red-600" />}
              <span className={`font-medium ${
                testResult.status === 'sent' ? 'text-emerald-700' :
                testResult.status === 'mock_sent' ? 'text-blue-700' :
                'text-red-700'
              }`}>
                {testResult.message}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Automated Notifications */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Settings size={20} />
          Automated Notification Triggers
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
            <div>
              <div className="font-medium">Compliance Status Changes</div>
              <div className="text-sm text-slate-500">Notify when a control status changes to &quot;gap&quot; or &quot;compliant&quot;</div>
            </div>
            <div className="text-sm text-slate-400">
              API: POST /api/notifications/teams/compliance-alert
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
            <div>
              <div className="font-medium">High-Risk Alerts</div>
              <div className="text-sm text-slate-500">Notify when a risk score exceeds critical threshold (20+)</div>
            </div>
            <div className="text-sm text-slate-400">
              API: POST /api/notifications/teams/risk-alert
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
            <div>
              <div className="font-medium">Policy Review Due</div>
              <div className="text-sm text-slate-500">Notify when policies are due for review</div>
            </div>
            <div className="text-sm text-slate-400">
              Coming soon
            </div>
          </div>
        </div>
        
        <p className="mt-4 text-sm text-slate-500">
          To enable automated notifications, integrate these API endpoints into your workflow automation tools or CI/CD pipelines.
        </p>
      </div>
    </DashboardLayout>
  );
}
